import { useEffect, useRef, useState, type FormEvent } from "react";

import { Card } from "../components/ui";
import { adminApi } from "./api";
import type { AdminToken } from "./types";

export function AdminLogin({ onToken }: { onToken: (token: AdminToken) => void }) {
  const emailRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => emailRef.current?.focus(), []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const token = await adminApi.login({
        email: email.trim().toLowerCase(),
        password,
        code: code.replace(/\s+/g, "") || undefined,
      });
      onToken(token);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't sign in. Try again.");
      setPassword("");
      setCode("");
      setBusy(false);
    }
  }

  return (
    <div className="adm-center">
      <Card title="Sign in">
        <form className="adm-form" onSubmit={submit} noValidate>
          {error && (
            <p className="notice notice-crit" role="alert">
              {error}
            </p>
          )}
          <label className="field">
            <span>Email</span>
            <input
              ref={emailRef}
              type="email"
              inputMode="email"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
            />
          </label>
          <label className="field">
            <span>Password</span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
            />
          </label>
          <label className="field">
            <span>Authenticator code (once it's set up)</span>
            <input
              className="adm-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={12}
              placeholder="6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={busy}
            />
          </label>
          <button className="btn btn-primary btn-block" type="submit" disabled={busy || !email.trim() || !password}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </Card>
    </div>
  );
}
